import { createRoute } from "@tanstack/solid-router";
import { createMemo, createSignal, Match, onCleanup, onMount, Show, Switch } from "solid-js";

import { rootRoute } from "../__root";
import { useSession } from "@/lib/auth";
import { sessionActor } from "@/lib/session-machine";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@listto/shared";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useListGroupsQuery, useListsWithMetaQuery } from "@listto/shared/solid";
import {
  closestCenter,
  DragDropProvider,
  DragDropSensors,
  type DragEvent,
  DragOverlay,
} from "@thisbeyond/solid-dnd";
import { List as ListIcon } from "lucide-solid";
import { AppLayoutContent } from "@/features/layout/components/app-layout-content";
import { OFFLINE_CACHE_STALE_TIME_MS, SESSION_STALE_TIME_MS } from "@/lib/constants";

export { useAppLayoutContext } from "@/features/layout/context";

function isSolidDndRegistryNoise(args: unknown[]): boolean {
  const [msg] = args;
  return (
    typeof msg === "string" &&
    msg.includes("nonexistent") &&
    (msg.includes("draggable") ||
      msg.includes("droppable") ||
      msg.includes("sensor") ||
      msg.includes("transformer"))
  );
}

function AppLayout() {
  onMount(() => {
    const originalWarn = console.warn;
    const originalError = console.error;

    console.warn = (...args: unknown[]) => {
      if (isSolidDndRegistryNoise(args)) {
        return;
      }
      originalWarn(...args);
    };

    console.error = (...args: unknown[]) => {
      if (isSolidDndRegistryNoise(args)) {
        return;
      }
      originalError(...args);
    };

    onCleanup(() => {
      console.warn = originalWarn;
      console.error = originalError;
    });
  });

  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const [activeDragId, setActiveDragId] = createSignal<string | null>(null);

  const lists = useListsWithMetaQuery({
    db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const listGroups = useListGroupsQuery({
    db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const handleDragStart = ({ draggable }: DragEvent) => {
    setActiveDragId(String(draggable.id));
  };

  const handleDragEnd = ({ draggable, droppable }: DragEvent) => {
    const id = String(draggable.id);
    setActiveDragId(null);

    if (!droppable || id === droppable.id) {
      return;
    }

    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }

    if (id.startsWith("list-") && String(droppable.id).startsWith("list-")) {
      // List reordering logic can be added here
      console.log("Reorder list:", id, "to", droppable.id);
    } else if (id.startsWith("todo-") && String(droppable.id).startsWith("todo-")) {
      // Todos reordering logic can be added here
      // Note: Components can also listen to these events if needed via context
      console.log("Reorder todo:", id, "to", droppable.id);
    }
  };

  const activeDragItem = createMemo(() => {
    const id = activeDragId();
    if (!id) {
      return null;
    }

    if (id.startsWith("list-")) {
      const listId = id.replace("list-", "");
      const list = lists.data?.find((l) => l.id === listId);
      return list ? { type: "list" as const, name: list.name } : null;
    }

    if (id.startsWith("todo-")) {
      return { type: "todo" as const, id: id.replace("todo-", "") };
    }

    return null;
  });

  return (
    <DragDropProvider
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetector={closestCenter}
    >
      <DragDropSensors />
      {/* The shell mounts immediately; `db` may be null while PowerSync's local
          DB initializes. AppLayoutContent renders the static chrome instantly and
          shows granular skeletons per data region until `db` (and each query)
          is ready. */}
      <AppLayoutContent
        db={db()}
        activeListId={
          activeDragId()?.startsWith("list-")
            ? (activeDragId()?.replace("list-", "") ?? null)
            : null
        }
        activeDragId={activeDragId}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        lists={lists}
        listGroups={listGroups}
      />

      <DragOverlay>
        <Show when={activeDragItem()}>
          {(item) => (
            <Switch>
              <Match when={item().type === "list"}>
                <div class="flex items-center gap-2.5 rounded-md bg-card border border-primary px-3 py-2.5 text-sm font-medium shadow-xl opacity-90 scale-105 transition-transform z-[1000]">
                  <ListIcon class="h-4 w-4 shrink-0" />
                  <span class="truncate text-xs">{item().name}</span>
                </div>
              </Match>
              <Match when={item().type === "todo"}>
                <div class="flex items-center gap-3 rounded-lg bg-card border border-primary p-4 shadow-2xl opacity-90 scale-105 w-[300px] z-[1000]">
                  <div class="h-5 w-5 rounded-full border-2 border-primary/30" />
                  <div class="h-3 w-32 bg-muted rounded" />
                </div>
              </Match>
            </Switch>
          )}
        </Show>
      </DragOverlay>
    </DragDropProvider>
  );
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
// When viewport preloads fire simultaneously for multiple child routes, they all
// share a single API request instead of each sending their own.
let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  // Wait for the machine to finish priming from storage.
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  const currentSnapshot = sessionActor.getSnapshot();
  // Machine may have recovered a session from localStorage during priming.
  // If we have a session OR we have already verified with the server, we skip.
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  // No session in machine — verify with the server once.
  // The redirect is thrown outside try/catch so TanStack Router receives it.
  const fetchSession = async () =>
    api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      // Add a timeout to avoid hanging the route transition if the server is slow/rate-limited
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        const SESSION_CHECK_TIMEOUT = 1500;
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT);
      });

      return await Promise.race([
        api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
        }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error o timeout al verificar sesión:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    // Network error / timeout — DO NOT send VERIFY_COMPLETE. Marking the
    // machine as verified would lock the app in visitor mode permanently,
    // because every subsequent route load would early-return on `isVerified`
    // and the in-component `useSession` query is enabled by `!isPriming()`
    // alone. Leaving `isVerified: false` lets the next navigation retry
    // and `useSession` retry on mount.
    return;
  }

  const { data, error, response } = sessionResult;

  // Sync with machine regardless of success/error (error will clear session)
  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({
    type: "SERVER_REPLY",
    session: sessionPayload,
    error: errorPayload,
  });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      // Server explicitly rejected auth — allow access as a visitor/guest.
      // (SERVER_REPLY above already handled this, but we return early)
      return;
    }
    // Other server error — allow offline access.
    // Ensure we are marked as verified if the above SERVER_REPLY was skipped or didn't set it.
    // (Actually SERVER_REPLY always sets isVerified: true in our machine)
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  beforeLoad: () => {
    const snapshot = sessionActor.getSnapshot();

    // Hot path: session already established and machine is done priming.
    // We only prefetch if the cached data is stale — useSession's createQuery
    // will handle refetching reactively once mounted.
    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;

      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    // Cold path: fire the verify in the background and let the route render
    // immediately. The InitialSyncOverlay covers the UI while the machine is
    // priming/verifying, and the AppLayout component reactively redirects to
    // /login if the verify resolves to an unauthenticated user. This matches
    // the behaviour of `apps/native`: routing is never gated on a network
    // round-trip, and the user never sees a frozen login form.
    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
    // Intentional fire-and-forget — see comment above.
  },

  component: AppLayout,
});
